
import React, { useState, useRef, useEffect } from 'react'
import { Button, Divider, Layout, Space, Tooltip, Typography } from 'antd'
import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import UILayout from './UILayout'
import SortableCard from './SortableCard'
import PreviewMenu from './PreviewMenu'
import { formatDateTime } from '../../Customs/formatDateTime'
import { PageBuilderServices } from '../../../Services/PageBuilderServices'
import { notify } from '../../../customs/NotifyProvider'
import { EyeTwoTone } from '@ant-design/icons'
import { ENDPOINTS } from '../../../Services/ENDPOINTS'
const { Sider, Content } = Layout

const PageBuilder_1 = ({ slug_id, record }) => {
  const [selectedLayouts, setSelectedLayouts] = useState([])
  const [formData, setFormData] = useState({})
  const cardRefs = useRef({})
  const contentRef = useRef(null)
  const [pcb_in_data, setPcb_in_data] = useState([])
  const [isLoading, setIsLoading] = useState(true) // Optional: to show loading state

  const getBuildPageBySlug = async (id) => {
    try {
      const request = await PageBuilderServices.pbc_get_by_slug(id)

      if (!request.data || request.data.length === 0) {
        setIsLoading(false)
        return
      }

      const rawContent = request.data[0].content

      const fixedString = "[" + rawContent.trim().slice(1, -1) + "]"
      const stringArray = JSON.parse(fixedString)
      const parsedContent = stringArray.map(item => JSON.parse(item))

      console.log("🎯 PURE JSON", parsedContent)

      setPcb_in_data(parsedContent)
    } catch (error) {
      console.error("Parsing failed:", error)
      notify.error("Failed to load page content")
    } finally {
      setIsLoading(false)
    }
  }

  // 🔥 Initialize layouts and form data from saved content
  useEffect(() => {
    if (pcb_in_data.length === 0) {
      setSelectedLayouts([])
      setFormData({})
      return
    }

    // Sort by index to ensure correct order
    const sortedContent = [...pcb_in_data].sort((a, b) => a.index - b.index)

    const newLayouts = []
    const newFormData = {}

    sortedContent.forEach((item) => {
      const uniqueId = `layout-${item.layout}-${item.index}-${Date.now()}` // Ensure uniqueness even if reordering later

      newLayouts.push({
        id: uniqueId,
        layoutId: item.layout, // e.g., 1, 2, 3...
      })

      newFormData[uniqueId] = item.data || {}
    })

    setSelectedLayouts(newLayouts)
    setFormData(newFormData)
  }, [pcb_in_data])

  useEffect(() => {
    getBuildPageBySlug(slug_id)
  }, [slug_id])

  // === Rest of your existing functions remain unchanged ===
  const handleLayoutSelect = (layoutId) => {
    const newId = `${layoutId}-${Date.now()}`
    setSelectedLayouts(prev => [...prev, { id: newId, layoutId }])
    setFormData(prev => ({ ...prev, [newId]: {} }))
  }

  const handleFormChange = (id, values) => {
    setFormData(prev => ({ ...prev, [id]: values }))
  }

  const handleDelete = (id) => {
    setSelectedLayouts(prev => prev.filter(i => i.id !== id))
    setFormData(prev => {
      const newData = { ...prev }
      delete newData[id]
      return newData
    })
    delete cardRefs.current[id]
  }

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    setSelectedLayouts(items => {
      const oldIndex = items.findIndex(i => i.id === active.id)
      const newIndex = items.findIndex(i => i.id === over.id)
      return arrayMove(items, oldIndex, newIndex)
    })
  }

  const scrollToCard = (id) => {
    const el = cardRefs.current[id]
    const container = contentRef.current
    if (!el || !container) return
    container.scrollTop = container.scrollTop // cancel ongoing
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handlePublish = async () => {
    try {
      const publishedData = selectedLayouts.map((item, arrayIndex) => ({
        index: arrayIndex + 1,
        layout: item.layoutId,
        data: formData[item.id] || {},
      }))

      const payload = {
        slug_id: slug_id,
        content: publishedData,
        created_at: formatDateTime(),
        updated_at: formatDateTime()
      }

      const request = await PageBuilderServices.pbc_insert(payload)

      if (request.status) {
        notify.success("Page Updated", request.message)
      } else {
        notify.warning("Something went wrong!", request.message)
      }
    } catch (error) {
      console.error(error)
      notify.error("Failed to publish page")
    }
  }

  // === Render ===
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Page Builder</h1>

        <Space>

          <Tooltip title="Save changes before preview">
            <Button
              icon={<EyeTwoTone />}
              onClick={() =>
                window.open(`${ENDPOINTS.HOST}${record.slug}`, "_blank", "noopener,noreferrer")
              }
            />
          </Tooltip>
          <Button type="primary" ghost onClick={handlePublish}>
            Save Changes
          </Button>

        </Space>
      </div>

      <Divider />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Typography.Text>Loading page content...</Typography.Text>
        </div>
      ) : (
        <Layout>
          <Sider width={300} theme="light" style={{ height: '80vh', overflowY: 'auto' }}>
            <UILayout onSelect={handleLayoutSelect} />
          </Sider>

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={selectedLayouts.map(i => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Content
                ref={contentRef}
                style={{
                  padding: 16,
                  paddingTop: 60,
                  height: '80vh',
                  overflowY: 'auto',
                }}
              >
                {selectedLayouts.length === 0 && (
                  <h3>Please select a layout</h3>
                )}
                {selectedLayouts.map(item => (
                  <SortableCard
                    key={item.id}
                    item={item}
                    onDelete={handleDelete}
                    onFormChange={handleFormChange}
                    formData={formData[item.id] || {}}
                    setRef={(el) => (cardRefs.current[item.id] = el)}
                  />
                ))}
              </Content>

              <Sider width={90} theme="light" style={{ height: '80vh', overflowY: 'auto' }}>
                <PreviewMenu items={selectedLayouts} onSelect={scrollToCard} />
              </Sider>
            </SortableContext>
          </DndContext>
        </Layout>
      )}
    </>
  )
}

export default PageBuilder_1